import { assert } from './helpers/assert.helper';
import { createElement } from './helpers/element.helper';
/**
 * Base class for all Jadis components.
 * It provides a structure for creating web components with a shadow DOM,
 * event handling, and attribute management.
 */
export class Jadis extends HTMLElement {
    static selector;
    static template = '';
    static observedAttributes = [];
    shadowRoot;
    attributesCallback = {};
    _abortController = new AbortController();
    _isConnected = false;
    constructor() {
        super();
        this.shadowRoot = this.attachShadow({ mode: 'open' });
        this.shadowRoot.appendChild(this.buildTemplate());
    }
    /**
     * Creates a new instance of the component.
     * @param attributes The attributes to set on the component
     * @param appendTo The element to append the component to
     * @returns The created component instance
     */
    static createElement(attributes = {}, appendTo) {
        return createElement(this.selector, attributes, appendTo);
    }
    /**
     * Registers the component as a custom element.
     * This method should be called once to define the custom element in the browser.
     * It checks if the selector is defined and if the custom element is not already registered.
     * @throws Will throw an error if the selector is not defined for the component
     */
    static register() {
        assert(this.selector, `selector is not defined for ${this.name}`);
        if (!customElements.get(this.typeOfClass.selector)) {
            customElements.define(this.typeOfClass.selector, this.typeOfClass);
        }
    }
    /**
     * Checks if the component is connected to the DOM.
     * @returns True if the component is connected, false otherwise
     */
    get isConnected() {
        return this._isConnected;
    }
    connectedCallback() {
        this._isConnected = true;
        setTimeout(() => this.onConnect?.());
    }
    disconnectedCallback() {
        this._abortController.abort();
        this.onDisconnect?.();
    }
    attributeChangedCallback(name, oldValue, newValue) {
        this.attributesCallback[name]?.(newValue, oldValue);
    }
    /**
     * Returns the AbortSignal associated with this component.
     * This signal can be used to cancel ongoing operations or event listeners.
     * @returns The AbortSignal for this component
     */
    get killSignal() {
        return this._abortController.signal;
    }
    /**
     * Retrieves an element from the component's template.
     * @param query The query string to find the element
     * @returns The found element
     * @throws Will throw an error if the element is not found
     */
    getElement(query) {
        const el = query
            .split('>>>')
            .reduce((nextEl, nextQuery) => {
            return (nextEl.shadowRoot ?? nextEl).querySelector(nextQuery);
        }, this);
        assert(el, `${query} element is not reachable`);
        return el;
    }
    /**
     * Toggles a class on the component based on a condition.
     * If the condition is true, the class will be added; if false, it will be removed.
     * @param className The name of the class to toggle
     * @param condition The binary condition to determine whether to add or remove the class
     */
    toggleClass(className, condition) {
        condition
            ? this.classList.add(className)
            : this.classList.remove(className);
    }
    /**
     * Registers a callback for a specific event on a bus.
     * @param bus The event bus to register the callback on
     * @param event The event key to listen for
     * @param callback The callback to invoke when the event is emitted
     */
    onBus(bus, event, callback) {
        bus.register(event, callback, this.killSignal);
    }
    /**
     * Creates a handler for events on the component.
     * This handler allows registering and emitting events in a type-safe manner.
     * @template EventType The type of events to handle
     * @returns An object with methods to register and emit events
     * @example
     * // Typescript usage:
     * const events = this.useEvents<{ someEvent: string }>();
     * events.register('someEvent', (detail) => {
     *   console.log('Event detail:', detail);
     * });
     * events.emit('someEvent', 'Hello World');
     *
     * // Javascript usage:
     * const events = this.useEvents({someEvent: String});
     * events.register('someEvent', (detail) => {
     *   console.log('Event detail:', detail);
     * });
     * events.emit('someEvent', 'Hello World');
     */
    useEvents(_schema) {
        return {
            register: (event, callback) => {
                const listener = ({ detail, }) => callback(detail);
                this.addEventListener(event, listener, {
                    signal: this.killSignal,
                });
            },
            emit: (event, ...params) => {
                this.dispatchEvent(new CustomEvent(event, { detail: params[0] }));
            },
        };
    }
    /**
     * Registers a callback for a specific event on an element.
     * @param element The element to listen for events on
     * @param event The event key to listen for
     * @param callback The callback to invoke when the event is emitted
     */
    on(element, event, callback) {
        element.addEventListener(event, callback, {
            signal: this.killSignal,
        });
    }
    buildTemplate() {
        const style = document.createElement('style');
        style.textContent = this.templateCss?.() ?? '';
        const fragment = document.createDocumentFragment();
        fragment.appendChild(style);
        const htmlContent = this.templateHtml?.();
        if (htmlContent) {
            fragment.appendChild(htmlContent);
        }
        return fragment;
    }
    static get typeOfClass() {
        return this.prototype.constructor;
    }
    get typeOfConstructor() {
        return this.constructor;
    }
}
